/**
 * AvatarCropModal — recadrage carré simple et déterministe
 *
 * Version réécrite from scratch pour éviter les états qui bloquent le bouton Valider.
 *
 * @version 2.7.9
 */

import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogBody, DialogFooter, Button } from './ui';

export interface AvatarCropModalProps {
  open: boolean;
  imageSrc: string | null;
  onOpenChange: (open: boolean) => void;
  onCancel: () => void;
  onConfirm: (blob: Blob) => Promise<void> | void;
}

type Point = { x: number; y: number };
type Size = { width: number; height: number };
type CropRect = { x: number; y: number; size: number };
type DragMode = 'move' | 'resize-nw' | 'resize-ne' | 'resize-sw' | 'resize-se' | null;

const MIN_SIZE = 88;
const HANDLE = 16;
const OUTPUT = 512;

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

function getContainRect(container: Size, natural: Size) {
  if (!container.width || !container.height || !natural.width || !natural.height) {
    return { x: 0, y: 0, width: 0, height: 0, scale: 1 };
  }

  const scale = Math.min(container.width / natural.width, container.height / natural.height);
  const width = natural.width * scale;
  const height = natural.height * scale;

  return {
    x: (container.width - width) / 2,
    y: (container.height - height) / 2,
    width,
    height,
    scale,
  };
}

export function AvatarCropModal({ open, imageSrc, onOpenChange, onCancel, onConfirm }: AvatarCropModalProps) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const dragRef = useRef<{
    mode: DragMode;
    pointerId: number;
    start: Point;
    startCrop: CropRect;
  } | null>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [natural, setNatural] = useState<Size>({ width: 0, height: 0 });
  const [container, setContainer] = useState<Size>({ width: 0, height: 0 });
  const [crop, setCrop] = useState<CropRect | null>(null);
  const [dragMode, setDragMode] = useState<DragMode>(null);

  const frame = useMemo(() => getContainRect(container, natural), [container, natural]);

  useEffect(() => {
    if (!open) return;
    setLoading(false);
    setError('');
    setNatural({ width: 0, height: 0 });
    setCrop(null);
    setDragMode(null);
    dragRef.current = null;
  }, [open, imageSrc]);

  useEffect(() => {
    if (!open) return;

    const measure = () => {
      const el = wrapRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setContainer({ width: rect.width, height: rect.width });
    };

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [open]);

  useEffect(() => {
    if (!open || !frame.width || !frame.height) return;
    setCrop((current) => {
      if (current) {
        const maxSize = Math.min(frame.width, frame.height);
        const size = clamp(current.size, MIN_SIZE, maxSize);
        const x = clamp(current.x, frame.x, frame.x + frame.width - size);
        const y = clamp(current.y, frame.y, frame.y + frame.height - size);
        return { x, y, size };
      }

      const maxSize = Math.min(frame.width, frame.height);
      const size = clamp(Math.round(maxSize * 0.7), MIN_SIZE, maxSize);
      return {
        x: frame.x + Math.round((frame.width - size) / 2),
        y: frame.y + Math.round((frame.height - size) / 2),
        size,
      };
    });
  }, [open, frame]);

  const startDrag = (mode: DragMode, event: ReactPointerEvent<HTMLDivElement>) => {
    if (!crop || !mode) return;
    event.preventDefault();
    event.stopPropagation();
    dragRef.current = {
      mode,
      pointerId: event.pointerId,
      start: { x: event.clientX, y: event.clientY },
      startCrop: crop,
    };
    setDragMode(mode);
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // not supported in all webviews
    }
  };

  const moveCrop = useCallback((clientX: number, clientY: number) => {
    const state = dragRef.current;
    if (!state || !crop || !frame.width || !frame.height) return;

    const dx = clientX - state.start.x;
    const dy = clientY - state.start.y;
    const maxSize = Math.min(frame.width, frame.height);

    if (state.mode === 'move') {
      setCrop({
        ...state.startCrop,
        x: clamp(state.startCrop.x + dx, frame.x, frame.x + frame.width - state.startCrop.size),
        y: clamp(state.startCrop.y + dy, frame.y, frame.y + frame.height - state.startCrop.size),
      });
      return;
    }

    let next = { ...state.startCrop };
    const anchorLeft = state.startCrop.x;
    const anchorTop = state.startCrop.y;
    const anchorRight = state.startCrop.x + state.startCrop.size;
    const anchorBottom = state.startCrop.y + state.startCrop.size;

    if (state.mode === 'resize-se') {
      const size = clamp(Math.max(state.startCrop.size + dx, state.startCrop.size + dy), MIN_SIZE, maxSize);
      next = { x: anchorLeft, y: anchorTop, size };
    } else if (state.mode === 'resize-sw') {
      const size = clamp(Math.max(state.startCrop.size - dx, state.startCrop.size + dy), MIN_SIZE, maxSize);
      next = { x: anchorRight - size, y: anchorTop, size };
    } else if (state.mode === 'resize-ne') {
      const size = clamp(Math.max(state.startCrop.size + dx, state.startCrop.size - dy), MIN_SIZE, maxSize);
      next = { x: anchorLeft, y: anchorBottom - size, size };
    } else if (state.mode === 'resize-nw') {
      const size = clamp(Math.max(state.startCrop.size - dx, state.startCrop.size - dy), MIN_SIZE, maxSize);
      next = { x: anchorRight - size, y: anchorBottom - size, size };
    }

    next.x = clamp(next.x, frame.x, frame.x + frame.width - next.size);
    next.y = clamp(next.y, frame.y, frame.y + frame.height - next.size);
    setCrop(next);
  }, [crop, frame.height, frame.width, frame.x, frame.y]);

  useEffect(() => {
    if (!dragMode) return;

    const onMove = (event: PointerEvent) => {
      if (!dragRef.current || event.pointerId !== dragRef.current.pointerId) return;
      moveCrop(event.clientX, event.clientY);
    };

    const onUp = (event: PointerEvent) => {
      if (!dragRef.current || event.pointerId !== dragRef.current.pointerId) return;
      dragRef.current = null;
      setDragMode(null);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [dragMode, moveCrop]);

  const handleConfirm = async () => {
    if (!imageSrc || !imgRef.current || !crop || !natural.width || !natural.height) {
      setError('L’image n’est pas encore prête.');
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = OUTPUT;
    canvas.height = OUTPUT;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setError('Impossible de préparer le recadrage.');
      return;
    }

    const sx = (crop.x - frame.x) / frame.scale;
    const sy = (crop.y - frame.y) / frame.scale;
    const sSize = crop.size / frame.scale;

    setLoading(true);
    setError('');

    try {
      ctx.drawImage(imgRef.current, sx, sy, sSize, sSize, 0, 0, OUTPUT, OUTPUT);
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92));
      if (!blob) {
        throw new Error('Impossible de générer l’image recadrée.');
      }

      await onConfirm(blob);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la validation.');
    } finally {
      setLoading(false);
    }
  };

  const overlayStyle: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    cursor: dragMode ? 'grabbing' : 'grab',
    touchAction: 'none',
  };

  const cropStyle: React.CSSProperties = crop
    ? {
        position: 'absolute',
        left: `${crop.x}px`,
        top: `${crop.y}px`,
        width: `${crop.size}px`,
        height: `${crop.size}px`,
        border: '2px solid #ffffff',
        borderRadius: '0.5rem',
        boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.40)',
        boxSizing: 'border-box',
        touchAction: 'none',
      }
    : { display: 'none' };

  const handleOuter: React.CSSProperties = {
    position: 'absolute',
    width: `${HANDLE * 2}px`,
    height: `${HANDLE * 2}px`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2,
    touchAction: 'none',
    userSelect: 'none',
  };

  const handleInner: React.CSSProperties = {
    width: `${HANDLE}px`,
    height: `${HANDLE}px`,
    borderRadius: '999px',
    border: '2px solid #002147',
    background: '#fff',
    boxShadow: '0 1px 2px rgba(0,0,0,0.18)',
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onCancel(); onOpenChange(next); }}>
      <DialogContent style={{ maxWidth: '28rem', padding: '0.8rem', maxHeight: '90vh' }}>
        <DialogHeader>
          <DialogTitle>Recadrer la photo</DialogTitle>
          <DialogDescription>Déplacez le carré, puis validez pour enregistrer l’avatar.</DialogDescription>
        </DialogHeader>

        <DialogBody style={{ maxHeight: '64vh', overflowY: 'auto', paddingBottom: '0.25rem' }}>
          <div
            ref={wrapRef}
            style={{
              position: 'relative',
              width: '100%',
              aspectRatio: '1 / 1',
              overflow: 'hidden',
              borderRadius: '0.8rem',
              border: '1px solid #e5e7eb',
              background: '#0f172a',
            }}
          >
            {imageSrc && (
              <img
                ref={imgRef}
                src={imageSrc}
                alt="Aperçu"
                onLoad={(e) => {
                  setNatural({ width: e.currentTarget.naturalWidth, height: e.currentTarget.naturalHeight });
                }}
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  userSelect: 'none',
                  pointerEvents: 'none',
                }}
              />
            )}

            {crop && (
              <div style={overlayStyle}>
                <div style={cropStyle} onPointerDown={(e) => startDrag('move', e)}>
                  <div style={{ ...handleOuter, left: `-${HANDLE * 0.5}px`, top: `-${HANDLE * 0.5}px`, cursor: 'nwse-resize' }} onPointerDown={(e) => startDrag('resize-nw', e)}>
                    <div style={handleInner} />
                  </div>
                  <div style={{ ...handleOuter, right: `-${HANDLE * 0.5}px`, top: `-${HANDLE * 0.5}px`, cursor: 'nesw-resize' }} onPointerDown={(e) => startDrag('resize-ne', e)}>
                    <div style={handleInner} />
                  </div>
                  <div style={{ ...handleOuter, left: `-${HANDLE * 0.5}px`, bottom: `-${HANDLE * 0.5}px`, cursor: 'nesw-resize' }} onPointerDown={(e) => startDrag('resize-sw', e)}>
                    <div style={handleInner} />
                  </div>
                  <div style={{ ...handleOuter, right: `-${HANDLE * 0.5}px`, bottom: `-${HANDLE * 0.5}px`, cursor: 'nwse-resize' }} onPointerDown={(e) => startDrag('resize-se', e)}>
                    <div style={handleInner} />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div style={{ marginTop: '0.9rem', display: 'grid', gap: '0.6rem' }}>
            <div style={{ fontSize: '0.8rem', color: '#6b7280', lineHeight: 1.4 }}>
              Faites glisser le carré pour cadrer l’image. Les coins redimensionnent le carré.
            </div>
            {error && (
              <div style={{ padding: '0.65rem 0.75rem', borderRadius: '0.5rem', border: '1px solid #fecaca', background: '#fef2f2', color: '#b91c1c', fontSize: '0.85rem', lineHeight: 1.4 }}>
                {error}
              </div>
            )}
          </div>
        </DialogBody>

        <DialogFooter style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button variant="outline" onClick={onCancel} disabled={loading} style={{ minWidth: '6.5rem', height: '2.35rem' }}>
            Annuler
          </Button>
          <Button onClick={handleConfirm} disabled={!imageSrc || loading || !crop} style={{ minWidth: '7rem', height: '2.35rem' }}>
            {loading ? 'Chargement...' : 'Valider'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AvatarCropModal;
